import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { formatMoney } from "../../shared/money";
import { hasPermission, isOwnerLevel } from "../../shared/permissions";
import { api } from "../api";
import { useSession } from "../App";
import { Banner, messageOf } from "./ui";

type Snapshot = {
  lines: { id: string; description: string; quantity_milli: number; unit_price_minor: number; taxable_net_minor: number; tax_minor: number | null; gross_minor: number | null; internal_cost_minor: number | null; client_visible: boolean; explanation?: string }[];
  totals: { net_minor: number; tax_minor: number | null; gross_minor: number | null; deposit_minor: number | null };
  issues: { code: string; severity: string; message: string }[];
  availability: { state: string; detail: string; subject_to_confirmation: boolean };
  blocks: { title: string; body: string }[];
  client_name: string;
  event_title: string;
  margin?: { status: string; margin_minor: number | null };
};
type ProposalDetail = {
  proposal: { id: string; number: string; status: string; event_id: string; language: string };
  revision_id: string | null;
  status: string | null;
  needs_review: number | null;
  snapshot: Snapshot | null;
  recipients: string[];
  cover_subject: string | null;
  outbox: { id: string; status: string; provider: string; last_error: string | null }[];
};

export function ProposalPage() {
  const { id = "" } = useParams();
  const { user } = useSession();
  const [detail, setDetail] = useState<ProposalDetail | null>(null);
  const [recipients, setRecipients] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [note, setNote] = useState<string | null>(null);
  const [grant, setGrant] = useState<string | null>(null);
  const canApprove = hasPermission(user.role, user.permissions, "proposals.approve");
  const canSend = hasPermission(user.role, user.permissions, "proposals.send");

  function load() {
    api<ProposalDetail>(`/api/proposals/${id}`)
      .then((result) => {
        setDetail(result);
        setRecipients(result.recipients.join(", "));
      })
      .catch((reason: unknown) => setError(messageOf(reason)));
  }

  useEffect(load, [id]);

  const revision = detail?.revision_id;
  function sendKey() {
    const storage = `send:${revision}`;
    const existing = sessionStorage.getItem(storage);
    if (existing) return existing;
    const created = crypto.randomUUID();
    sessionStorage.setItem(storage, created);
    return created;
  }

  async function post(path: string, body: Record<string, unknown>) {
    setError(null);
    try {
      const result = await api<Record<string, unknown>>(path, { method: "POST", body: JSON.stringify(body) });
      setNote(typeof result.error === "string" ? result.error : "Saved.");
      if (typeof result.path === "string" && typeof result.token === "string") setGrant(`${window.location.origin}/p/${result.token}`);
      if (result.status === "captured") setNote(typeof result.error === "string" ? result.error : "Δεν στάλθηκε email. Η πρόταση δεν σημειώθηκε ως απεσταλμένη.");
      load();
    } catch (reason) {
      setError(messageOf(reason));
    }
  }

  return (
    <>
      <header className="page-head">
        <div>
          <p className="meta">{detail ? <Link to={`/events/${detail.proposal.event_id}`}>Εκδήλωση</Link> : "Πρόταση"}</p>
          <h1>{detail?.proposal.number ?? "Πρόταση"}</h1>
          <p className="lede">{detail?.snapshot?.event_title} · {detail?.status}{detail?.needs_review ? " · χρειάζεται έλεγχο" : ""} · {detail?.proposal.language === "el" ? "Ελληνικά" : "Αγγλικά"}</p>
        </div>
      </header>
      <Banner error={error} note={note} />
      {detail?.needs_review ? <p className="banner">Μια τιμή ή η εκδήλωση άλλαξε μετά την έγκριση. Υπολόγισε ξανά, ή κατέγραψε εξαίρεση τιμής από τον ιδιοκτήτη. Το PDF που στάλθηκε, αν υπάρχει, μένει ως έχει.</p> : null}
      {!detail && !error ? <p>Φόρτωση πρότασης…</p> : null}
      {detail?.snapshot ? (
        <div className="split">
          <section className="panel">
            <h2>{detail.snapshot.client_name}</h2>
            <p className={`pill ${detail.snapshot.availability.state}`}>{detail.snapshot.availability.state}</p>
            <p>{detail.snapshot.availability.detail}</p>
            {detail.snapshot.blocks.map((block) => <p key={block.title}><strong>{block.title}.</strong> {block.body}</p>)}
            <table>
              <thead><tr><th>Γραμμή</th><th className="num">Καθαρά</th><th className="num">Κόστος</th></tr></thead>
              <tbody>
                {detail.snapshot.lines.filter((line) => line.client_visible).map((line) => (
                  <tr key={line.id}>
                    <td>{line.description}<div className="meta">{line.quantity_milli / 1000} × {formatMoney(line.unit_price_minor)}</div></td>
                    <td className="num">{formatMoney(line.taxable_net_minor)}</td>
                    <td className="num">{line.internal_cost_minor == null ? "Αγνωστο" : formatMoney(line.internal_cost_minor)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            <p>Καθαρά {formatMoney(detail.snapshot.totals.net_minor)} · φόρος {detail.snapshot.totals.tax_minor == null ? "εκκρεμεί" : formatMoney(detail.snapshot.totals.tax_minor)} · μικτά {detail.snapshot.totals.gross_minor == null ? "εκκρεμεί" : formatMoney(detail.snapshot.totals.gross_minor)}</p>
            <p className="meta">Προκαταβολή στο στιγμιότυπο: {detail.snapshot.totals.deposit_minor == null ? "εκκρεμεί" : formatMoney(detail.snapshot.totals.deposit_minor)}. Περιθώριο {detail.snapshot.margin?.status}{detail.snapshot.margin?.margin_minor == null ? "" : ` ${formatMoney(detail.snapshot.margin.margin_minor)}`}.</p>
            {detail.snapshot.issues.map((issue) => <p key={issue.code} className="banner">{issue.message}</p>)}
          </section>
          <aside className="stack">
            <section className="panel">
              <h2>Ελεγχος και αποστολή</h2>
              <label>Παραλήπτες<input value={recipients} onChange={(event) => setRecipients(event.target.value)} /></label>
              <div className="actions">
                {canApprove ? <button type="button" onClick={() => revision && void post(`/api/revisions/${revision}/approve`, { recipients: recipients.split(",").map((item) => item.trim()).filter(Boolean) })}>Εγκριση</button> : null}
                {isOwnerLevel(user.role) ? <button className="secondary" type="button" onClick={() => revision && void post(`/api/revisions/${revision}/honor`, { reason: "Κράτησε την τιμή που έχει ήδη δει ο πελάτης." })}>Κράτησε την τιμή</button> : null}
                {canSend ? <button type="button" onClick={() => revision && void post(`/api/revisions/${revision}/send`, { idempotency_key: sendKey() })}>Αποστολή</button> : null}
                {canSend ? <button className="secondary" type="button" onClick={() => revision && void post(`/api/revisions/${revision}/grant`, {})}>Σύνδεσμος πελάτη</button> : null}
              </div>
              <p className="meta">Η αποστολή χρησιμοποιεί ένα κλειδί για αυτή την καρτέλα, οπότε το διπλό κλικ επαναλαμβάνει την ίδια προσπάθεια. Το εξερχόμενο email μένει τοπική καταγραφή ώσπου να ρυθμιστεί πάροχος.</p>
              {grant ? <p><a href={grant}>{grant}</a></p> : null}
              {detail.outbox.map((item) => <p key={item.id}>{item.provider}: {item.status}. {item.last_error}</p>)}
              {revision ? (
                <div className="actions">
                  <a className="button secondary" href={`/api/revisions/${revision}/html`} target="_blank" rel="noreferrer">HTML</a>
                  <a className="button secondary" href={`/api/revisions/${revision}/pdf`} target="_blank" rel="noreferrer">PDF</a>
                  <button className="secondary" type="button" onClick={() => revision && void post(`/api/revisions/${revision}/recalculate`, {})}>Επανυπολογισμός</button>
                </div>
              ) : null}
            </section>
            {revision ? <iframe className="proposal-frame" title="Προεπισκόπηση πρότασης" src={`/api/revisions/${revision}/html?audience=client`} /> : null}
          </aside>
        </div>
      ) : null}
    </>
  );
}
